import { Box, Tooltip, Typography } from '@mui/material';
import { alpha, useTheme, type Theme } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useTranslation } from 'react-i18next';
import type { InspectionItemCondition } from '@/types';

interface ConditionIndicatorProps {
  condition: InspectionItemCondition | null;
  size?: 'small' | 'medium' | 'large';
  showLabel?: boolean;
}

const getConditionConfig = (theme: Theme): Record<
  InspectionItemCondition,
  { color: string; labelKey: string; icon: typeof CheckCircleIcon }
> => ({
  good: { color: theme.palette.success.main, labelKey: 'condition.good', icon: CheckCircleIcon },
  fair: { color: theme.palette.warning.main, labelKey: 'condition.fair', icon: WarningIcon },
  poor: { color: theme.palette.warning.dark, labelKey: 'condition.poor', icon: WarningIcon },
  critical: { color: theme.palette.error.main, labelKey: 'condition.critical', icon: ErrorIcon },
});

const sizeConfig = {
  small: { iconSize: 16, fontSize: '0.7rem' },
  medium: { iconSize: 20, fontSize: '0.8rem' },
  large: { iconSize: 24, fontSize: '0.875rem' },
};

export function ConditionIndicator({
  condition,
  size = 'medium',
  showLabel = false,
}: ConditionIndicatorProps) {
  const { t } = useTranslation('inspection');
  const theme = useTheme();
  const conditionConfig = getConditionConfig(theme);
  const { iconSize, fontSize } = sizeConfig[size];

  if (!condition) {
    return (
      <Tooltip title={t('condition.notInspected')}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <HelpOutlineIcon sx={{ fontSize: iconSize, color: 'text.disabled' }} />
          {showLabel && (
            <Typography variant="caption" sx={{ fontSize, color: 'text.disabled' }}>
              {t('condition.notInspected')}
            </Typography>
          )}
        </Box>
      </Tooltip>
    );
  }

  const config = conditionConfig[condition];
  const Icon = config.icon;

  return (
    <Tooltip title={t(config.labelKey)}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Icon sx={{ fontSize: iconSize, color: config.color }} />
        {showLabel && (
          <Typography variant="caption" sx={{ fontSize, color: config.color, fontWeight: 500 }}>
            {t(config.labelKey)}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
}

// Condition selector for editing
interface ConditionSelectorProps {
  value: InspectionItemCondition | null;
  onChange: (condition: InspectionItemCondition) => void;
  disabled?: boolean;
}

export function ConditionSelector({ value, onChange, disabled }: ConditionSelectorProps) {
  const { t } = useTranslation('inspection');
  const theme = useTheme();
  const conditionConfig = getConditionConfig(theme);
  const conditions: InspectionItemCondition[] = ['good', 'fair', 'poor', 'critical'];

  return (
    <Box sx={{ display: 'flex', gap: 1 }}>
      {conditions.map((condition) => {
        const config = conditionConfig[condition];
        const Icon = config.icon;
        const isSelected = value === condition;

        return (
          <Tooltip key={condition} title={t(config.labelKey)}>
            <Box
              onClick={() => !disabled && onChange(condition)}
              sx={{
                p: 1,
                borderRadius: 1,
                border: 2,
                borderColor: isSelected ? config.color : 'divider',
                backgroundColor: isSelected ? alpha(config.color, 0.08) : 'transparent',
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.5 : 1,
                transition: 'all 0.15s',
                '&:hover': !disabled
                  ? {
                      borderColor: config.color,
                      backgroundColor: alpha(config.color, 0.06),
                    }
                  : undefined,
              }}
            >
              <Icon sx={{ fontSize: 24, color: config.color, display: 'block' }} />
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  );
}
